import { RowItemType, Section, SelectorIdentifier, UIElementType } from '../../models';
import * as actions from '../../actions';
import { ChannelElement, FlowType, FlowTypeOption, MonitoringPointElement } from '../../../models/location-card.model';
import { FlexSectionIds, FlexInfoFieldId } from './base-config-models';
import { Action } from '@ngrx/store';
import { field } from '../../core/field-schema';
import { pattern, required } from '../../core/validator-builders';
import { FlexLocationCardState } from './models';
import {
    AvailableDynamicSection,
    RegisterEntityType,
    RegisterEntityTypeOption,
} from '../../models/dynamic-section-models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { MonitorSeriesNames } from '../../../location-card.constants';

const NONE_SENSOR_NAME = 'None';

/**
 * FLEX Info Configuration Section
 */
export const flexInfo = (state: FlexLocationCardState): Section => {
    const settings = state.settings;
    const details = state.locationDetails;

    const config = state.monitorConfiguration;
    const isEditMode = details && details.length > 0 && details[0].series === MonitorSeriesNames.flex && config;

    return {
        info: {
            id: FlexSectionIds.FlexInfo,
            title: 'ADD_EDIT.ADD_EDIT_FLEX',
            displayTitle: true,
            expandable: true,
            expanded: false,
        },
        rows: [
            // Sensor 1 Title Row
            {
                info: {
                    id: 'sensor1-title-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: 'sensor1-title',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'sensor1Title',
                        label: 'ADD_EDIT.ADD_EDIT_SUBTITLE',
                        panelClass: 'font-600 section-subtitle subtitle-text font-600',
                    },
                ],
            },

            // Sensor 1 Fields Row
            {
                info: {
                    id: 'sensor1-fields-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.select<AvailableDynamicSection, string>(FlexInfoFieldId.Sensor1, 'sensor', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SENSOR',
                        value: null,
                        validators: [required()],
                        panelClass: 'width-30',
                        valueMapper: (value) => value.name,
                        displayMapper: (value) => value.name,
                        events: {
                            onChange: (sensor: AvailableDynamicSection) => [
                                actions.flexSensorChange({ sensor, index: 0 }),
                            ],
                        },
                        optionsSelector: SelectorIdentifier.DynamicSections,
                    }),
                    field.select<MonitoringPointElement, number>(FlexInfoFieldId.MP1, 'mp', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MONITORING_POINT',
                        value: null,
                        panelClass: 'width-30',
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        validators: [required()],
                        events: {
                            onChange: () => [actions.flexMonitorPointChange({ index: 0 })],
                        },
                        optionsSelector: SelectorIdentifier.MonitoringPoints,
                    }),
                    field.select<ChannelElement, number>(FlexInfoFieldId.Channel1, 'channel', {
                        panelClass: 'width-30',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_CHANNEL',
                        value: null,
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        validators: [required()],
                        events: {
                            onChange: () => [actions.flexChannelChange({ index: 0 })],
                        },
                        optionsSelector: SelectorIdentifier.Channels,
                    }),
                ],
            },
            {
                info: {
                    id: 'sensor1-silt-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    {
                        id: 'silt-input-section',
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'silt-input-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'silt1-input-row',
                                        panelClass: 'custom-flat-button flex',
                                    },
                                    items: [
                                        field.number(FlexInfoFieldId.Silt1, 'silt', {
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SILT',
                                            readonly: true,
                                            placeholderSuffix: settings.depthUnit,
                                            value:
                                                isEditMode && config.silts && config.silts.length ? config.silts[0] : 0,
                                            panelClass: 'slide-active-display',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.installationValuePattern,
                                                    message: 'COMMON.OFFSET_NON_METRIC_ERROR',
                                                }),
                                            ],
                                        }),
                                        {
                                            id: FlexInfoFieldId.SiltEditButton1,
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Button,
                                            disabled: !isEditMode,
                                            name: 'siltEditButton1',
                                            iconName: 'edit',
                                            tooltip: 'COMMON.SILT_EDITOR',
                                            panelClass: 'no-margin',
                                            events: {
                                                onClick: () => [
                                                    actions.openSiltDialog({ updateFieldId: FlexInfoFieldId.Silt1, mpIndex: 0 }),
                                                ],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                    field.select<FlowTypeOption, FlowType>(FlexInfoFieldId.FlowType1, 'flowType', {
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_FLOW',
                        value: isEditMode && config ? config.monitorPoint1FlowType : FlowType.Normal,
                        visible: false,
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.FlexFlowType,
                    }),
                ],
            },

            {
                info: {
                    id: 'mp1-sensor-preferences-title-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: FlexInfoFieldId.Mp1SensorPreferencesTitle,
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'mp1SensorPreferencesTitle',
                        visible: false,
                        label: 'ADD_EDIT.MP1_SENSOR_PREFERENCES',
                        panelClass: 'font-600 section-subtitle subtitle-text',
                    },
                ],
            },

            {
                info: {
                    id: 'mp1-sensor-preferences-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<RegisterEntityTypeOption, RegisterEntityType>(
                        FlexInfoFieldId.Mp1DepthEntityType,
                        'depthEntityType',
                        {
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_DEPTH_ENTITY_TYPE',
                            value: isEditMode && config?.mp1Preferences?.depthEntityType
                                ? config.mp1Preferences.depthEntityType
                                : null,
                            panelClass: 'custom-width',
                            visible: false,
                            disabled: true,
                            valueMapper: (value) => value.value,
                            displayMapper: (value) => value.text,
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.Mp1DepthEntityTypes,
                            events: {
                                onChange: (entityType: RegisterEntityTypeOption) => [
                                    actions.updateMp1DepthEntityType({ entityType: entityType.value }),
                                ],
                            },
                        }
                    ),
                    field.select<RegisterEntityTypeOption, RegisterEntityType>(
                        FlexInfoFieldId.Mp1VelocityEntityType,
                        'velocityEntityType',
                        {
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_VELOCITY_ENTITY_TYPE',
                            value: isEditMode && config?.mp1Preferences?.velocityEntityType
                                ? config.mp1Preferences.velocityEntityType
                                : null,
                            panelClass: 'custom-width',
                            visible: false,
                            disabled: true,
                            valueMapper: (value) => value.value,
                            displayMapper: (value) => value.text,
                            validators: [required()],
                            optionsSelector: SelectorIdentifier.Mp1VelocityEntityTypes,
                            events: {
                                onChange: (entityType: RegisterEntityTypeOption) => [
                                    actions.updateMp1VelocityEntityType({ entityType: entityType.value }),
                                ],
                            },
                        }
                    ),
                ],
            },

            // Divider
            {
                info: {
                    id: 'divider-row',
                    panelClass: '',
                },
                items: [
                    {
                        id: 'divider',
                        type: RowItemType.UI,
                        uiType: UIElementType.Divider,
                        name: 'divider',
                        dividerStyle: 'solid',
                        dividerThickness: 1,
                        dividerColor: '#e0e0e0',
                        panelClass: 'divider margin-175-0',
                    },
                ],
            },

            // Sensor 2 Title Row
            {
                info: {
                    id: 'sensor2-title-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: 'sensor2-title',
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'sensor2Title',
                        visible: false,
                        label: 'ADD_EDIT.ADD_EDIT_SECOND_SUBTITLE',
                        panelClass: 'section-subtitle subtitle-text font-600',
                    },
                ],
            },

            // Sensor 2 Fields Row
            {
                info: {
                    id: 'sensor2-fields-row',
                    panelClass: 'custom-33p',
                },
                items: [
                    field.select<AvailableDynamicSection, string>(FlexInfoFieldId.Sensor2, 'sensor', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SENSOR',
                        panelClass: 'width-30',
                        valueMapper: (value) => value.name,
                        displayMapper: (value) => value.name,
                        value: null,
                        visible: false,
                        events: {
                            onChange: (sensor: AvailableDynamicSection) => {
                                const toDispatch: Action[] = [actions.flexSensorChange({ sensor, index: 1 })];

                                if (sensor.name !== NONE_SENSOR_NAME) {
                                    toDispatch.push(
                                        actions.enableField({ fieldId: FlexInfoFieldId.MP2 }),
                                        actions.enableField({ fieldId: FlexInfoFieldId.Channel2 }),
                                    );
                                }

                                return toDispatch;
                            },
                        },
                        optionsSelector: SelectorIdentifier.DynamicSectionsMp2,
                    }),
                    field.select<MonitoringPointElement, number>(FlexInfoFieldId.MP2, 'mp', {
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_MONITORING_POINT',
                        panelClass: 'width-30',
                        value: null,
                        disabled: true,
                        visible: false,
                        events: {
                            onChange: () => [actions.flexMonitorPointChange({ index: 1 })],
                        },
                        optionsSelector: SelectorIdentifier.MonitoringPoints,
                    }),
                    field.select<ChannelElement, number>(FlexInfoFieldId.Channel2, 'channel', {
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_CHANNEL',
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        panelClass: 'width-30',
                        value: null,
                        visible: false,
                        disabled: true,
                        events: {
                            onChange: () => [actions.flexChannelChange({ index: 1 })],
                        },
                        optionsSelector: SelectorIdentifier.Channels,
                    }),
                ],
            },
            {
                info: {
                    id: 'sensor2-silt-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    {
                        id: 'silt2-input-section',
                        type: RowItemType.Section,
                        section: {
                            info: {
                                id: 'silt2-input-container',
                                title: '',
                                displayTitle: false,
                                expandable: false,
                                panelClass: 'custom-width',
                            },
                            rows: [
                                {
                                    info: {
                                        id: 'silt2-input-row',
                                        panelClass: 'custom-flat-button flex',
                                    },
                                    items: [
                                        field.number(FlexInfoFieldId.Silt2, 'silt', {
                                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_SILT',
                                            readonly: true,
                                            placeholderSuffix: settings.depthUnit,
                                            visible: false,
                                            value:
                                                isEditMode && config.silts && config.silts.length ? config.silts[1] : 0,
                                            panelClass: 'slide-active-display',
                                            validators: [
                                                pattern({
                                                    value: REGEX_CONFIG.installationValuePattern,
                                                    message: 'COMMON.OFFSET_NON_METRIC_ERROR',
                                                }),
                                            ],
                                        }),
                                        {
                                            id: FlexInfoFieldId.SiltEditButton2,
                                            type: RowItemType.UI,
                                            uiType: UIElementType.Button,
                                            disabled: !isEditMode,
                                            name: 'siltEditButton2',
                                            visible: false,
                                            iconName: 'edit',
                                            tooltip: 'COMMON.SILT_EDITOR',
                                            panelClass: 'no-margin',
                                            events: {
                                                onClick: () => [
                                                    actions.openSiltDialog({ updateFieldId: FlexInfoFieldId.Silt2, mpIndex: 1 }),
                                                ],
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    },
                    field.select<FlowTypeOption, FlowType>(FlexInfoFieldId.FlowType2, 'flowType', {
                        panelClass: 'custom-width',
                        placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_FLOW',
                        value: (isEditMode && config && config.monitorPoint2FlowType !== undefined) ? config.monitorPoint2FlowType : FlowType.Normal,
                        valueMapper: (value) => value.value,
                        displayMapper: (value) => value.text,
                        visible: false,
                        validators: [required()],
                        optionsSelector: SelectorIdentifier.FlexFlowType,
                    }),
                ],
            },

            {
                info: {
                    id: 'mp2-sensor-preferences-title-row',
                    panelClass: 'full-width',
                },
                items: [
                    {
                        id: FlexInfoFieldId.Mp2SensorPreferencesTitle,
                        type: RowItemType.UI,
                        uiType: UIElementType.Text,
                        name: 'mp2SensorPreferencesTitle',
                        visible: false,
                        label: 'ADD_EDIT.MP2_SENSOR_PREFERENCES',
                        panelClass: 'font-600 section-subtitle subtitle-text',
                    },
                ],
            },

            {
                info: {
                    id: 'mp2-sensor-preferences-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.select<RegisterEntityTypeOption, RegisterEntityType>(
                        FlexInfoFieldId.Mp2DepthEntityType,
                        'depthEntityType',
                        {
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_DEPTH_ENTITY_TYPE',
                            value: isEditMode && config?.mp2Preferences?.depthEntityType
                                ? config.mp2Preferences.depthEntityType
                                : null,
                            panelClass: 'custom-width',
                            visible: false,
                            disabled: true,
                            valueMapper: (value) => value.value,
                            displayMapper: (value) => value.text,
                            optionsSelector: SelectorIdentifier.Mp2DepthEntityTypes,
                            events: {
                                onChange: (entityType: RegisterEntityTypeOption) => [
                                    actions.updateMp2DepthEntityType({ entityType: entityType.value }),
                                ],
                            },
                        }
                    ),
                    field.select<RegisterEntityTypeOption, RegisterEntityType>(
                        FlexInfoFieldId.Mp2VelocityEntityType,
                        'velocityEntityType',
                        {
                            placeholder: 'ADD_EDIT.INPUT_PLACEHOLDER_VELOCITY_ENTITY_TYPE',
                            value: isEditMode && config?.mp2Preferences?.velocityEntityType
                                ? config.mp2Preferences.velocityEntityType
                                : null,
                            panelClass: 'custom-width',
                            visible: false,
                            disabled: true,
                            valueMapper: (value) => value.value,
                            displayMapper: (value) => value.text,
                            optionsSelector: SelectorIdentifier.Mp2VelocityEntityTypes,
                            events: {
                                onChange: (entityType: RegisterEntityTypeOption) => [
                                    actions.updateMp2VelocityEntityType({ entityType: entityType.value }),
                                ],
                            },
                        }
                    ),
                ],
            },
        ],
    };
};
